import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Keyboard, Check, ChevronRight, ChevronLeft, Circle, ArrowRight, ArrowLeft, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { MainLayout } from "@/components/layout/MainLayout";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";
import { Slider } from "@/components/ui/slider";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";

type SetupStage = "config" | "recording" | "testing";

interface SwitchAction {
  id: string;
  label: string;
  icon: React.ElementType;
  key?: string;
  code?: string;
}

const getSwitchActions = (count: number, t: (key: string) => string): SwitchAction[] => {
  const actions: SwitchAction[] = [];
  
  if (count >= 1) {
    actions.push({ id: "select", label: t('selectAction'), icon: Check });
  }
  if (count >= 2) {
    actions.push({ id: "cancel", label: t('cancelAction'), icon: RotateCcw });
  }
  if (count >= 3) {
    actions.push({ id: "next", label: t('nextAction'), icon: ArrowLeft });
    actions[1] = { id: "previous", label: t('previousAction'), icon: ArrowRight };
  }
  if (count >= 4) {
    actions.push({ id: "back", label: t('backAction'), icon: RotateCcw });
  }
  if (count >= 5) {
    return [
      { id: "select", label: t('selectAction'), icon: Check },
      { id: "up", label: t('upAction'), icon: ArrowUp },
      { id: "down", label: t('downAction'), icon: ArrowDown },
      { id: "left", label: t('leftAction'), icon: ArrowLeft },
      { id: "right", label: t('rightAction'), icon: ArrowRight },
    ];
  }
  
  return actions;
};

const switchCountDescriptionKeys: Record<number, string> = {
  1: "switch1Desc",
  2: "switch2Desc",
  3: "switch3Desc",
  4: "switch4Desc",
  5: "switch5Desc",
};

export default function ExternalSwitchSetup() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<SetupStage>("config");
  
  // Config state
  const [switchCount, setSwitchCount] = useState(1);
  const [scanMode, setScanMode] = useState<"cell_by_cell" | "row_and_column">("cell_by_cell");
  const [scanType, setScanType] = useState<"automatic" | "manual">("automatic");
  const [scanSpeed, setScanSpeed] = useState(3.0);
  
  // Recording state
  const [switchMappings, setSwitchMappings] = useState<Record<string, { key: string; code: string }>>({});
  const [recordingFor, setRecordingFor] = useState<string | null>(null);
  
  // Testing state
  const [testedSwitches, setTestedSwitches] = useState<Set<string>>(new Set());
  const [lastPressed, setLastPressed] = useState<string | null>(null);

  const settings = getUserSettings();
  const isFemale = settings.gender === "female";

  // Load saved settings
  useEffect(() => {
    const saved = getUserSettings();
    if (saved.externalSwitchCount) setSwitchCount(saved.externalSwitchCount);
    if (saved.scanMode) setScanMode(saved.scanMode);
    if (saved.externalSwitchScanType) setScanType(saved.externalSwitchScanType);
    if (saved.scanSpeed) setScanSpeed(saved.scanSpeed);
    if (saved.externalSwitchMappings) setSwitchMappings(saved.externalSwitchMappings);
  }, []);

  const { t, isRTL, dir } = useCaregiverTranslation();
  const switchActions = getSwitchActions(switchCount, t);

  // Recording key handler
  const handleKeyForRecording = useCallback((e: KeyboardEvent) => {
    if (!recordingFor) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const mapping = { key: e.key, code: e.code };
    setSwitchMappings(prev => ({ ...prev, [recordingFor]: mapping }));
    setRecordingFor(null);
  }, [recordingFor]);

  // Testing key handler
  const handleKeyForTesting = useCallback((e: KeyboardEvent) => {
    if (stage !== "testing") return;
    
    // Find which action this key corresponds to
    for (const [actionId, mapping] of Object.entries(switchMappings)) {
      if (mapping.code === e.code || mapping.key === e.key) {
        setTestedSwitches(prev => new Set([...prev, actionId]));
        setLastPressed(actionId);
        setTimeout(() => setLastPressed(null), 500);
        break;
      }
    }
  }, [stage, switchMappings]);

  useEffect(() => {
    if (recordingFor) {
      window.addEventListener("keydown", handleKeyForRecording);
      return () => window.removeEventListener("keydown", handleKeyForRecording);
    }
  }, [recordingFor, handleKeyForRecording]);

  useEffect(() => {
    if (stage === "testing") {
      window.addEventListener("keydown", handleKeyForTesting);
      return () => window.removeEventListener("keydown", handleKeyForTesting);
    }
  }, [stage, handleKeyForTesting]);

  const handleBack = () => {
    if (stage === "config") {
      navigate("/control");
    } else if (stage === "recording") {
      setStage("config");
    } else if (stage === "testing") {
      setStage("recording");
    }
  };

  const handleContinue = () => {
    if (stage === "config") {
      // Save config and go to recording
      const settings = getUserSettings();
      saveUserSettings({
        ...settings,
        externalSwitchCount: switchCount,
        scanMode,
        externalSwitchScanType: scanType,
        scanSpeed,
      });
      setStage("recording");
    } else if (stage === "recording") {
      // Check all switches are mapped
      const allMapped = switchActions.every(action => switchMappings[action.id]);
      if (allMapped) {
        const settings = getUserSettings();
        saveUserSettings({
          ...settings,
          externalSwitchMappings: switchMappings,
        });
        setTestedSwitches(new Set());
        setStage("testing");
      }
    } else if (stage === "testing") {
      // All tested, save and navigate home
      const settings = getUserSettings();
      saveUserSettings({
        ...settings,
        controlMethod: "external_switch",
        externalSwitchCount: switchCount,
        scanMode,
        externalSwitchScanType: scanType,
        scanSpeed,
        externalSwitchMappings: switchMappings,
      });
      navigate("/");
    }
  };

  const allSwitchesMapped = switchActions.every(action => switchMappings[action.id]);
  const allSwitchesTested = switchActions.every(action => testedSwitches.has(action.id));

  const canContinue = stage === "config" 
    ? true 
    : stage === "recording" 
      ? allSwitchesMapped 
      : allSwitchesTested;

  const renderConfigStage = () => (
    <>
      {/* Switch Count Selection */}
      <div className="w-full max-w-2xl mb-6">
        <p className={cn("text-sm text-muted-foreground mb-3", isRTL ? "text-right" : "text-left")}>
          {isFemale ? t("selectSwitchCountFemale") : t("selectSwitchCountMale")}
        </p>
        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((num) => (
            <button
              key={num}
              onClick={() => setSwitchCount(num)}
              className={cn(
                "flex flex-col items-center p-4 rounded-xl border-2 transition-all",
                switchCount === num
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border hover:border-primary/30 text-foreground"
              )}
            >
              <span className="text-2xl font-bold">{num}</span>
              <span className="text-xs mt-1">
                {num === 1 ? t("switchLabel") : t("switchesLabel")}
              </span>
            </button>
          ))}
        </div>
        <p className="text-sm text-muted-foreground text-center mt-3">
          {t(switchCountDescriptionKeys[switchCount] as any)}
        </p>
      </div>

      {/* Scan Mode */}
      <div className="w-full max-w-2xl mb-4">
        <p className={cn("text-sm text-muted-foreground mb-2", isRTL ? "text-right" : "text-left")}>{t("scanMode")}</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setScanMode("cell_by_cell")}
            className={cn(
              "flex flex-col items-center p-3 rounded-xl border-2 transition-all",
              scanMode === "cell_by_cell"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border hover:border-primary/30 text-foreground"
            )}
          >
            <span className="font-medium text-sm">{t("cellByCellMode")}</span>
            <span className={cn(
              "text-xs mt-1",
              scanMode === "cell_by_cell" ? "text-primary-foreground/70" : "text-muted-foreground"
            )}>
              {t("cellByCellDesc")}
            </span>
          </button>
          <button
            onClick={() => setScanMode("row_and_column")}
            className={cn(
              "flex flex-col items-center p-3 rounded-xl border-2 transition-all",
              scanMode === "row_and_column"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border hover:border-primary/30 text-foreground"
            )}
          >
            <span className="font-medium text-sm">{t("rowAndColumnMode")}</span>
            <span className={cn(
              "text-xs mt-1",
              scanMode === "row_and_column" ? "text-primary-foreground/70" : "text-muted-foreground"
            )}>
              {t("rowAndColumnDesc")}
            </span>
          </button>
        </div>
      </div>

      {/* Scan Type - Only for 2+ switches */}
      {switchCount >= 2 && (
        <div className="w-full max-w-2xl mb-4">
          <p className={cn("text-sm text-muted-foreground mb-2", isRTL ? "text-right" : "text-left")}>{t("scanType")}</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setScanType("automatic")}
              className={cn(
                "flex flex-col items-center p-3 rounded-xl border-2 transition-all",
                scanType === "automatic"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border hover:border-primary/30 text-foreground"
              )}
            >
              <span className="font-medium text-sm">{t("automaticScanType")}</span>
              <span className={cn(
                "text-xs mt-1",
                scanType === "automatic" ? "text-primary-foreground/70" : "text-muted-foreground"
              )}>
                {t("cursorMovesAutoSelect")}
              </span>
            </button>
            <button
              onClick={() => setScanType("manual")}
              className={cn(
                "flex flex-col items-center p-3 rounded-xl border-2 transition-all",
                scanType === "manual"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border hover:border-primary/30 text-foreground"
              )}
            >
              <span className="font-medium text-sm">{t("manualScanType")}</span>
              <span className={cn(
                "text-xs mt-1",
                scanType === "manual" ? "text-primary-foreground/70" : "text-muted-foreground"
              )}>
                {t("cursorMovesOnPress")}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Scan Speed - Only for automatic mode */}
      {(switchCount === 1 || scanType === "automatic") && (
        <div className="w-full max-w-2xl mb-6">
          <p className={cn("text-sm text-muted-foreground mb-2", isRTL ? "text-right" : "text-left")}>{t("scanSpeedLabel")}</p>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">{t("slow")}</span>
              <span className="text-sm font-medium text-foreground">{scanSpeed.toFixed(1)}s</span>
              <span className="text-xs text-muted-foreground">{t("fast")}</span>
            </div>
            <Slider
              value={[scanSpeed]}
              onValueChange={(value) => setScanSpeed(value[0])}
              min={0.5}
              max={5}
              step={0.5}
              className="w-full"
            />
          </div>
        </div>
      )}
    </>
  );

  const renderRecordingStage = () => (
    <div className="w-full max-w-2xl">
      <p className="text-sm text-muted-foreground text-center mb-6">
        {isFemale ? t("pressSwitchToRecordFemale") : t("pressSwitchToRecord")}
      </p>
      
      <div className="grid grid-cols-1 gap-3">
        {switchActions.map((action) => {
          const Icon = action.icon;
          const mapping = switchMappings[action.id];
          const isRecording = recordingFor === action.id;
          
          return (
            <motion.button
              key={action.id}
              onClick={() => setRecordingFor(action.id)}
              className={cn(
                "flex items-center gap-4 p-4 rounded-xl border-2 transition-all",
                isRecording
                  ? "bg-orange-500/20 border-orange-500 ring-4 ring-orange-500/30"
                  : mapping
                    ? "bg-primary/10 border-primary"
                    : "bg-card border-border hover:border-primary/30"
              )}
              animate={isRecording ? { scale: [1, 1.02, 1] } : {}}
              transition={{ repeat: isRecording ? Infinity : 0, duration: 1 }}
            >
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center",
                isRecording ? "bg-orange-500/20" : mapping ? "bg-primary/20" : "bg-muted"
              )}>
                <Icon className={cn(
                  "w-6 h-6",
                  isRecording ? "text-orange-500" : mapping ? "text-primary" : "text-muted-foreground"
                )} />
              </div>
              
              <div className={cn("flex-1", isRTL ? "text-right" : "text-left")}>
                <span className={cn(
                  "font-medium",
                  isRecording ? "text-orange-500" : "text-foreground"
                )}>
                  {action.label}
                </span>
                {mapping && !isRecording && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("recordedLabel").replace("{0}", mapping.key === " " ? "Space" : mapping.key)}
                  </p>
                )}
                {isRecording && (
                  <p className="text-xs text-orange-500 mt-1">
                    {isFemale ? t("pressSwitchPromptFemale") : t("pressSwitchPrompt")}
                  </p>
                )}
              </div>
              
              {mapping && !isRecording && (
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-5 h-5 text-primary-foreground" />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );

  const renderTestingStage = () => (
    <div className="w-full max-w-2xl">
      <p className="text-sm text-muted-foreground text-center mb-6">
        {isFemale ? t("pressSwitchToTestFemale") : t("pressSwitchToTest")}
      </p>
      
      <div className="grid grid-cols-1 gap-3">
        {switchActions.map((action) => {
          const Icon = action.icon;
          const isTested = testedSwitches.has(action.id);
          const isPressed = lastPressed === action.id;
          
          return (
            <motion.div
              key={action.id}
              className={cn(
                "flex items-center gap-4 p-4 rounded-xl border-2 transition-all",
                isPressed
                  ? "bg-orange-500/20 border-orange-500 scale-105"
                  : isTested
                    ? "bg-primary/10 border-primary"
                    : "bg-card border-border"
              )}
              animate={isPressed ? { scale: [1, 1.05, 1] } : {}}
            >
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center",
                isPressed ? "bg-orange-500/20" : isTested ? "bg-primary/20" : "bg-muted"
              )}>
                <Icon className={cn(
                  "w-6 h-6",
                  isPressed ? "text-orange-500" : isTested ? "text-primary" : "text-muted-foreground"
                )} />
              </div>
              
              <div className={cn("flex-1", isRTL ? "text-right" : "text-left")}>
                <span className="font-medium text-foreground">{action.label}</span>
                <p className="text-xs text-muted-foreground mt-1">
                  {switchMappings[action.id]?.key === " " ? "Space" : switchMappings[action.id]?.key}
                </p>
              </div>
              
              {isTested ? (
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-5 h-5 text-primary-foreground" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Circle className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {allSwitchesTested && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 bg-primary/10 border-2 border-primary/30 rounded-xl text-center"
        >
          <p className="text-primary font-medium">
            {t("allSwitchesRecognized")}
          </p>
        </motion.div>
      )}
    </div>
  );

  const getStageTitle = () => {
    switch (stage) {
      case "config": return t("switchSetupTitle");
      case "recording": return t("recordingSwitchesTitle");
      case "testing": return t("testingSwitchesTitle");
    }
  };

  const getStageNumber = () => {
    switch (stage) {
      case "config": return 1;
      case "recording": return 2;
      case "testing": return 3;
    }
  };

  return (
    <MainLayout>
      <div 
        className="flex-1 flex flex-col items-center p-4 bg-background overflow-auto"
        dir={dir}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg">
            <Keyboard className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              {getStageTitle()}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t("stageXOfY").replace("{0}", String(getStageNumber())).replace("{1}", "3")}
            </p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center gap-2 mb-6">
          {["config", "recording", "testing"].map((s, i) => (
            <React.Fragment key={s}>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all",
                stage === s
                  ? "bg-primary text-primary-foreground"
                  : getStageNumber() > i + 1
                    ? "bg-primary/20 text-primary"
                    : "bg-muted text-muted-foreground"
              )}>
                {i + 1}
              </div>
              {i < 2 && (
                <div className={cn(
                  "w-8 h-1 rounded",
                  getStageNumber() > i + 1 ? "bg-primary/50" : "bg-muted"
                )} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Stage Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="w-full flex flex-col items-center"
          >
            {stage === "config" && renderConfigStage()}
            {stage === "recording" && renderRecordingStage()}
            {stage === "testing" && renderTestingStage()}
          </motion.div>
        </AnimatePresence>

        {/* Action buttons */}
        <div className="flex gap-3 mt-auto pb-4">
          <button
            onClick={handleBack}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-card hover:bg-muted border-2 border-border",
              "text-muted-foreground hover:text-foreground"
            )}
          >
            {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            <span>{t("back")}</span>
          </button>

          <button
            onClick={handleContinue}
            disabled={!canContinue}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            )}
          >
            <Check className="w-5 h-5" />
            <span>{stage === "testing" ? t("finish") : t("continue")}</span>
          </button>
        </div>
      </div>
    </MainLayout>
  );
}